import React, { useState, useEffect, useRef } from "react";
import { FiMessageSquare } from "react-icons/fi";
import AiAssistantPanel from "./AiAssistantPanel";
import useFocusTrap from "../hooks/useFocusTrap";
import "./AiWidget.css";

const AiAssistantWidget = ({ isOpen, onToggle }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [hasOpened, setHasOpened] = useState(false);
    const widgetRef = useRef(null);

    useFocusTrap(widgetRef, isOpen);

    // Check first visit
    useEffect(() => {
        const hasSeenTooltip = localStorage.getItem("ai_tooltip_seen");
        if (!hasSeenTooltip) {
            setShowTooltip(true);
            // Hide tooltip after 8 seconds or when opened
            const t = setTimeout(() => {
                setShowTooltip(false);
                localStorage.setItem("ai_tooltip_seen", "true");
            }, 8000);
            return () => clearTimeout(t);
        }
    }, []);

    // Handle open
    useEffect(() => {
        if (isOpen) {
            setShowTooltip(false);
            localStorage.setItem("ai_tooltip_seen", "true");
            if (!hasOpened) setHasOpened(true);
        }
    }, [isOpen, hasOpened]);

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (isOpen && widgetRef.current && !widgetRef.current.contains(e.target)) {
                // Find if click was on the floating button (which is outside widgetRef usually if rendered separately)
                // Actually, we'll wrap everything in a container or check classnames
                if (!e.target.closest('.ai-fab')) {
                    onToggle(false);
                }
            }
        };

        if (isOpen) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, onToggle]);

    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === "Escape") onToggle(false);
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onToggle]);

    return (
        <>
            <div className="ai-widget-container">
                {/* Tooltip */}
                <div className={`ai-tooltip ${showTooltip ? "visible" : ""}`}>
                    Ask me about my projects! 👋
                </div>

                {/* Floating Button */}
                <button
                    className={`ai-fab ${isOpen ? "active" : ""}`}
                    onClick={() => onToggle(!isOpen)}
                    aria-label="Toggle AI Assistant"
                >
                    {isOpen ? "×" : <FiMessageSquare className="ai-icon" />}
                </button>
            </div>

            {/* Drawer / Modal */}
            <div className={`ai-drawer-backdrop ${isOpen ? "open" : ""}`} onClick={() => onToggle(false)} aria-hidden="true"></div>
            <div className={`ai-drawer ${isOpen ? "open" : ""}`} ref={widgetRef} role="dialog" aria-modal="true" aria-label="AI Assistant">
                {(isOpen || hasOpened) && <AiAssistantPanel onClose={() => onToggle(false)} />}
            </div>
        </>
    );
};

export default AiAssistantWidget;
